import React from 'react';
import type { NodeProps } from 'reactflow';
import { useFocalState } from '../hooks/useFocalState';

// v0.4 Task 5: dispatcher factory.
//
// Picks rich vs compact based on focal state. Registers in
// nodes/index.ts like so:
//
//   byKind('peer', withFocalDispatch(PeerNodeCompact, PeerNodeRich))
//
// Rich variant receives { id, data } — simpler props than NodeProps
// since ReactFlow concerns (selection, drag) don't apply to rich
// cards. Compact variant receives the full NodeProps<D> so v0.3
// behavior is preserved.
//
// HoverCard wrapping lives INSIDE each compact component (v0.3
// behavior). Rich variants don't wrap in HoverCard — the card IS
// the deep-dive per spec §Design principle 5.
export function withFocalDispatch<D extends { turnKey?: string | null }>(
  Compact: React.FC<NodeProps<D>>,
  Rich: React.FC<{ id: string; data: D }>,
): React.FC<NodeProps<D>> {
  const Dispatcher: React.FC<NodeProps<D>> = (props) => {
    const { isFocal } = useFocalState();
    if (isFocal(props.data.turnKey)) {
      return <Rich id={props.id} data={props.data} />;
    }
    return <Compact {...props} />;
  };
  Dispatcher.displayName = `FocalDispatch(${
    Compact.displayName ?? Compact.name ?? 'Compact'
  }, ${Rich.displayName ?? Rich.name ?? 'Rich'})`;
  return Dispatcher;
}
